import React, { ReactNode, useState } from 'react';
import { AppHeader, AppShell, Button } from '@origo/origo-ui';
import { useAppDispatch, useAppSelector } from '../store/storeHooks';
import { selectEiendomsvelgerFocus } from '../store/uiSlice';
import { selectUser } from '@origo/auth';
import { setSelectedLocation, useMapViewContext } from '@origo/map-context';
import Point from '@arcgis/core/geometry/Point.js';
import { AdminSidePanel } from './AdminSidePanel';
import { MapLayersToggle } from './MapLayersToggle';
import { SearchSuggestionLayer } from '@origo/origo-components';
import geodataLogo from '../assets/geodata-logo.svg';
import { VersionTrigger, ChangelogDialog, NewVersionNotice, useNewVersionNotice, AddressSearch, useAddressSearch, type AddressSuggestion } from '@origo/origo-components';
import changelogMarkdown from '../../CHANGELOG.md?raw';
import styles from './AppLayout.module.css';


export interface AppLayoutProps {
    mapContent: ReactNode;
    onSignOut?: () => void;
    eiendommerUrl?: string;
    kommunenavn?: string;
    sokefilter?: Record<string, string>;
}

function getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

const AppLayout: React.FC<AppLayoutProps> = ({ mapContent, onSignOut, eiendommerUrl, kommunenavn, sokefilter }) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const [changelogOpen, setChangelogOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const { hasNewVersion, dismiss } = useNewVersionNotice('dp-admin.lastSeenVersion');

    const { mapView } = useMapViewContext();
    // Map-first fokus-modus: skjul adressesøk når eiendomsvelgeren er åpen.
    const eiendomsvelgerFocus = useAppSelector(selectEiendomsvelgerFocus);

    const {
        input,
        suggestions,
        isFetchingDetails,
        handleInputChange,
        handleSuggestionClick,
    } = useAddressSearch({ kommunenavn, searchFilter: sokefilter });

    const onSuggestionClick = (suggestion: AddressSuggestion) => {
        handleSuggestionClick(suggestion);
        dispatch(setSelectedLocation({
            selected: true,
            name: suggestion.text,
            address: suggestion.address,
            postalCode: suggestion.postalCode,
            postalArea: suggestion.postalArea,
            x: suggestion.x,
            y: suggestion.y,
            wkid: suggestion.wkid,
        }));
        mapView?.goTo({
            target: new Point({ x: suggestion.x, y: suggestion.y, spatialReference: { wkid: suggestion.wkid } }),
            zoom: 16,
        });
    };

    const openChangelog = () => {
        setChangelogOpen(true);
        dismiss();
    };

    return (
        <AppShell
            className={styles.adminShell}
            header={
                <AppHeader
                    logo={<img src={geodataLogo} alt="Geodata" width={34} height={32} />}
                    title="Drømmeplan Admin"
                    subtitle="Administrasjon av drømmeplaner"
                    center={
                        eiendomsvelgerFocus ? undefined : (
                            <AddressSearch
                                input={input}
                                suggestions={suggestions}
                                isFetchingDetails={isFetchingDetails}
                                onInputChange={handleInputChange}
                                onSuggestionClick={onSuggestionClick}
                                onHoverChange={setHoveredIndex}
                            />
                        )
                    }
                    user={user?.fullName ? {
                        name: user.fullName,
                        initials: getInitials(user.fullName),
                        avatarUrl: user.thumbnail,
                    } : undefined}
                    actions={
                        <>
                            <VersionTrigger placement="header" onClick={openChangelog} />
                            {onSignOut && (
                                <Button variant="ghost" size="sm" onClick={onSignOut}>
                                    Logg ut
                                </Button>
                            )}
                        </>
                    }
                />
            }
        >
            <div className={styles.mainColumn}>
                {hasNewVersion && (
                    <NewVersionNotice onOpenChangelog={openChangelog} onDismiss={dismiss} />
                )}
                <div className={styles.body}>
                    <AdminSidePanel eiendommerUrl={eiendommerUrl} />
                    <div className={styles.mapArea}>
                        <div className={styles.mapContainer}>
                            {mapContent}
                            <SearchSuggestionLayer
                                suggestions={suggestions}
                                hoveredIndex={hoveredIndex}
                                onPinClick={onSuggestionClick}
                            />
                            {eiendomsvelgerFocus && (
                                <div className={styles.mapHint} role="status">
                                    <span>Klikk eiendommer for å velge</span>
                                    <span className={styles.mapHintSep} aria-hidden="true" />
                                    <span>Esc for å avslutte</span>
                                </div>
                            )}
                            <MapLayersToggle />
                        </div>
                    </div>
                </div>
            </div>
            <ChangelogDialog
                open={changelogOpen}
                onClose={() => setChangelogOpen(false)}
                appName="Drømmeplan Admin"
                changelogMarkdown={changelogMarkdown}
            />
        </AppShell>
    );
};

export default AppLayout;
